import {useState} from 'react'
import {useDialogStore} from '@ariakit/react'
import {useNavigate} from 'react-router-dom'
import dayjs from 'dayjs'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Download01Icon, AlertCircleIcon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {usePermissions} from '@/features/auth/store/store'
import {routes} from '@/utilities/constants/routes'
import {SiarBulkExportType} from '@/features/projects/siar-bulk-export/types'
import {DuplicateExportError} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {useStoreSiarBulkExport} from '@/features/projects/siar-bulk-export/queries/useStoreSiarBulkExport'
import {
    StyledBulkSiarExportDateRow,
    StyledBulkSiarExportDialogBody,
    StyledBulkSiarExportDialogFooter,
    StyledBulkSiarExportDuplicateActions,
    StyledBulkSiarExportDuplicateAlert,
    StyledBulkSiarExportRadioGroup
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

const formatPayloadDate = (date: Date | null): string | undefined =>
    date ? dayjs(date).format('YYYY-MM-DD') : undefined

const buildExportFileName = (exportType: SiarBulkExportType, dateFrom: Date | null, dateTo: Date | null): string => {
    const prefix = exportType === 'open_project' ? 'SIAR-Apertura' : 'SIAR-Chiusura'
    const fmt = (d: Date) => dayjs(d).format('DDMMYYYY')
    if (dateFrom && dateTo) return `${prefix}-${fmt(dateFrom)}-${fmt(dateTo)}`
    if (dateFrom) return `${prefix}-${fmt(dateFrom)}`
    if (dateTo) return `${prefix}-${fmt(dateTo)}`
    return prefix
}

export const BulkSiarExportDialog = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    if (!permissions?.projects?.write) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="secondary" onClick={() => setIsOpen(true)}>
                        <Download01Icon />
                        {t('projects:bulk_siar_export_button')}
                    </Button>
                }
            />
            <Dialog width="480px">
                <DialogHeader>
                    <DialogHeading>{t('projects:bulk_siar_export_dialog_title')}</DialogHeading>
                    <DialogDismiss />
                </DialogHeader>
                {isOpen && <BulkSiarExportDialogContent onClose={() => dialogStore.setOpen(false)} />}
            </Dialog>
        </DialogProvider>
    )
}

const BulkSiarExportDialogContent = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [exportType, setExportType] = useState<SiarBulkExportType | null>(null)
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [duplicateError, setDuplicateError] = useState<DuplicateExportError | null>(null)

    const isDateRangeInvalid = !!(dateFrom && dateTo && dayjs(dateFrom).isAfter(dayjs(dateTo), 'day'))
    const isFormValid = exportType !== null && (dateFrom !== null || dateTo !== null) && !isDateRangeInvalid

    const mutation = useStoreSiarBulkExport({
        onSuccess: data => {
            if (!exportType) return
            if (data.size === 0) {
                toast.info(t('projects:bulk_siar_export_empty_response'))
                onClose()
                return
            }
            const file = buildFile({
                extension: 'zip',
                fileName: buildExportFileName(exportType, dateFrom, dateTo),
                data
            })
            downloadFile(file)
            onClose()
        },
        onError: (error: AxiosError<DuplicateExportError>) => {
            const body = error.response?.data
            if (error.response?.status === 409 && body?.code === 'DUPLICATE_EXPORT') {
                setDuplicateError(body)
                return
            }
            toast.error(t('errors:default'))
        }
    })

    const handleSubmit = () => {
        if (!isFormValid || !exportType) return
        setDuplicateError(null)
        mutation.mutate({
            exportType,
            dateFrom: formatPayloadDate(dateFrom),
            dateTo: formatPayloadDate(dateTo)
        })
    }

    const handleGoToHistory = () => {
        const fromStr = formatPayloadDate(dateFrom)
        const toStr = formatPayloadDate(dateTo)
        const fields = [
            ...(exportType ? [{key: 'exportType', operator: 'in', value: [exportType]}] : []),
            ...(fromStr ? [{key: 'dateFrom', operator: '=', value: fromStr}] : []),
            ...(toStr ? [{key: 'dateTo', operator: '=', value: toStr}] : [])
        ]
        const params = new URLSearchParams()
        if (fields.length > 0) params.set('filters', JSON.stringify({operator: 'and', fields}))
        const queryString = params.toString()
        navigate(`${routes.SIAR_EXPORT_HISTORY.handle.path}${queryString ? `?${queryString}` : ''}`)
        onClose()
    }

    const handleExportTypeChange = (value: SiarBulkExportType) => {
        setExportType(value)
        setDuplicateError(null)
    }

    return (
        <>
            <StyledBulkSiarExportDialogBody>
                <Flexbox direction="column" gap={2}>
                    <Label>{t('projects:bulk_siar_export_type_label')}</Label>
                    <StyledBulkSiarExportRadioGroup>
                        <RadioField
                            name="bulk-siar-export-type"
                            value="open_project"
                            checked={exportType === 'open_project'}
                            onChange={() => handleExportTypeChange('open_project')}
                            label={t('projects:bulk_siar_export_type_open')}
                        />
                        <RadioField
                            name="bulk-siar-export-type"
                            value="close_project"
                            checked={exportType === 'close_project'}
                            onChange={() => handleExportTypeChange('close_project')}
                            label={t('projects:bulk_siar_export_type_close')}
                        />
                    </StyledBulkSiarExportRadioGroup>
                </Flexbox>
                <StyledBulkSiarExportDateRow>
                    <InputDate
                        label={t('projects:bulk_siar_export_date_from')}
                        placeholder="gg/mm/aaaa"
                        value={dateFrom}
                        onChange={date => {
                            setDateFrom(date)
                            setDuplicateError(null)
                        }}
                        fullWidth
                        portal
                    />
                    <InputDate
                        label={t('projects:bulk_siar_export_date_to')}
                        placeholder="gg/mm/aaaa"
                        value={dateTo}
                        onChange={date => {
                            setDateTo(date)
                            setDuplicateError(null)
                        }}
                        fullWidth
                        portal
                    />
                </StyledBulkSiarExportDateRow>
                {isDateRangeInvalid && <InputHelpText error={t('projects:bulk_siar_export_date_validation_error')} />}
                {duplicateError && (
                    <StyledBulkSiarExportDuplicateAlert>
                        <Flexbox align="center" gap={2}>
                            <AlertCircleIcon size={16} />
                            <strong>{t('projects:bulk_siar_export_duplicate_error')}</strong>
                        </Flexbox>
                        <StyledBulkSiarExportDuplicateActions>
                            <Button variant="secondary" size="sm" onClick={handleGoToHistory}>
                                {t('projects:bulk_siar_export_go_to_history')}
                            </Button>
                        </StyledBulkSiarExportDuplicateActions>
                    </StyledBulkSiarExportDuplicateAlert>
                )}
            </StyledBulkSiarExportDialogBody>
            <StyledBulkSiarExportDialogFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                <Button variant="primaryNeutral" onClick={handleSubmit} disabled={!isFormValid || mutation.isPending}>
                    {t('projects:bulk_siar_export_submit')}
                    {mutation.isPending && <Spinner />}
                </Button>
            </StyledBulkSiarExportDialogFooter>
        </>
    )
}
